import { z } from 'zod';

/**
 * Rótulo do centro de custo no formato do NetSuite: "código - nome" (ex.: "010211008 - ART 3D").
 * O nome se repete entre empresas (o mesmo "ART 3D" na LOOT-BR e na PUBLISHER); o código, não,
 * porque o prefixo indica a empresa. Por isso toda tela, seletor e exportação mostra os dois juntos,
 * sempre por estas funções. Só exibição: o código nunca é gravado dentro do nome.
 */
export const COST_CENTER_LABEL_SEPARATOR = ' - ';

/** "código - nome"; sem nome, só o código; sem código, só o nome; sem nenhum, texto vazio. */
export function costCenterLabel(
  code: string | null | undefined,
  name: string | null | undefined,
): string {
  const c = code?.trim() ?? '';
  const n = name?.trim() ?? '';
  return c && n ? `${c}${COST_CENTER_LABEL_SEPARATOR}${n}` : c || n;
}

/**
 * Centro ativo sem a pessoa que aprova (o gestor do centro; o responsável é outro campo). É a mesma
 * conta no cartão da Visão geral de Dados Mestres e na lista que ele abre (ADR 0097).
 */
export function isCostCenterWithoutApprover(c: {
  isActive: boolean;
  managerUserId: string | null;
}): boolean {
  return c.isActive && !c.managerUserId;
}

/**
 * A busca encontra o centro pelo código, pelo nome ou pelo rótulo inteiro ("010211008 - art"), sem
 * diferença entre maiúsculas e minúsculas. Busca vazia encontra todos.
 */
export function costCenterMatches(
  search: string,
  code: string | null | undefined,
  name: string | null | undefined,
): boolean {
  const needle = search.trim().toLowerCase();
  if (!needle) return true;
  return [code, name, costCenterLabel(code, name)].some((v) => v?.toLowerCase().includes(needle));
}

/** Ordem dos seletores: pelo código, que já agrupa por empresa (prefixo 01, 02, 03...). */
export const byCostCenterCode = (a: { code: string }, b: { code: string }) =>
  a.code.localeCompare(b.code);

// ---------------------------------------------------------------------------
// Níveis e responsáveis (ADR 0047)
// ---------------------------------------------------------------------------

/**
 * Nível do centro na estrutura da empresa: Master (Head ou Diretor), Gerencial (gerente ou
 * coordenador; opcional) e Operacional (equipe ou atividade). Vazio = ainda sem nível.
 * Organiza, consolida e filtra: nunca dá alçada de aprovação.
 */
export const COST_CENTER_LEVELS = ['master', 'managerial', 'operational'] as const;
export type CostCenterLevel = (typeof COST_CENTER_LEVELS)[number];

/** Níveis aceitos para o centro superior de cada nível (o banco confere a mesma regra). */
export const COST_CENTER_PARENT_LEVELS: Record<CostCenterLevel, readonly CostCenterLevel[]> = {
  master: [],
  managerial: ['master'],
  operational: ['master', 'managerial'],
};

/** O que a estrutura precisa saber de cada centro. */
export interface CostCenterStructureNode {
  id: string;
  parentId: string | null;
  structureLevel: string | null;
  /** Gestor do centro: a conta que aprova. */
  managerUserId: string | null;
  managerName?: string | null;
  /** Só no Master: referência organizacional diferente do gestor (ADR 0099). */
  masterReferencePersonId: string | null;
  masterReferenceName?: string | null;
}

/** Onde o centro fica na estrutura e quem é o Master Responsável dele, calculado (ADR 0099). */
export interface CostCenterPlacement {
  /** Centro Master acima (ou o próprio, se for Master). */
  masterId: string | null;
  /** Centro Gerencial: o próprio, se for Gerencial, ou o superior Gerencial de um Operacional. */
  managerialId: string | null;
  /** Gestor do centro Master. */
  masterManagerUserId: string | null;
  /** Referência organizacional do Master, quando é outra pessoa; ela vale no lugar do gestor. */
  masterReferencePersonId: string | null;
  /** Nome do Master Responsável: a referência, se houver; senão, o gestor do Master. */
  masterResponsibleName: string | null;
}

/** Posição de cada centro na estrutura. Centros sem nível não têm Master nem Gerencial. */
export function costCenterPlacement(
  nodes: CostCenterStructureNode[],
): Map<string, CostCenterPlacement> {
  const byId = new Map(nodes.map((n) => [n.id, n]));
  const masterOf = (n: CostCenterStructureNode): string | null => {
    let cur: CostCenterStructureNode | undefined = n;
    for (let i = 0; cur && i < 50; i++) {
      if (cur.structureLevel === 'master') return cur.id;
      if (!cur.structureLevel) return null;
      cur = cur.parentId ? byId.get(cur.parentId) : undefined;
    }
    return null;
  };
  const managerialOf = (n: CostCenterStructureNode): string | null => {
    if (n.structureLevel === 'managerial') return n.id;
    const parent = n.parentId ? byId.get(n.parentId) : undefined;
    return n.structureLevel === 'operational' && parent?.structureLevel === 'managerial'
      ? parent.id
      : null;
  };
  const out = new Map<string, CostCenterPlacement>();
  for (const n of nodes) {
    const masterId = masterOf(n);
    const master = masterId ? byId.get(masterId) : undefined;
    out.set(n.id, {
      masterId,
      managerialId: managerialOf(n),
      masterManagerUserId: master?.managerUserId ?? null,
      masterReferencePersonId: master?.masterReferencePersonId ?? null,
      masterResponsibleName: master?.masterReferencePersonId
        ? (master.masterReferenceName ?? null)
        : (master?.managerName ?? null),
    });
  }
  return out;
}

/** Quem pede "Meus centros": a conta e, se estiver ligada, a pessoa da base de pessoas. */
export interface CostCenterViewer {
  userId: string | null;
  personId: string | null;
}

/**
 * Master e Gerencial de um centro (adendo 2 do ADR 0047), calculados só pelo superior direto
 * (`parent_id`): superior Master → o Master; superior Gerencial → o Gerencial e o Master acima
 * dele. Nada além do superior direto é gravado, para não existirem duas hierarquias.
 */
export function costCenterHierarchyOf(
  parentId: string | null,
  byId: ReadonlyMap<string, { parentId: string | null; structureLevel: string | null }>,
): { masterId: string | null; managerialId: string | null } {
  const parent = parentId ? byId.get(parentId) : undefined;
  if (parent?.structureLevel === 'master') return { masterId: parentId, managerialId: null };
  if (parent?.structureLevel === 'managerial') {
    const above = parent.parentId ? byId.get(parent.parentId) : undefined;
    return {
      masterId: above?.structureLevel === 'master' ? parent.parentId : null,
      managerialId: parentId,
    };
  }
  return { masterId: null, managerialId: null };
}

/**
 * O superior direto gravado a partir do Master e do Gerencial escolhidos: Master não tem; Gerencial
 * fica abaixo do Master; Operacional fica abaixo do Gerencial ou, sem Gerencial, direto no Master.
 */
export function costCenterParentFor(
  level: CostCenterLevel,
  masterId: string | null,
  managerialId: string | null,
): string | null {
  if (level === 'master') return null;
  if (level === 'managerial') return masterId;
  return managerialId ?? masterId;
}

/**
 * Recorte de centros de custo dos filtros: todos, "Meus centros" (onde a pessoa é gestora ou Master
 * Responsável, em qualquer empresa), os de um centro Master ou os de
 * um centro Gerencial. Filtra o que cada módulo já mostra: não amplia acesso nem soma duas vezes.
 */
export const COST_CENTER_SCOPES = ['all', 'mine', 'master', 'managerial'] as const;
export type CostCenterScope = (typeof COST_CENTER_SCOPES)[number];

/** Campos do recorte para os filtros dos módulos (query ou corpo da exportação). */
export const costCenterScopeFields = {
  costCenterScope: z.enum(COST_CENTER_SCOPES).optional(),
  costCenterScopeId: z.uuid().optional(),
};

/**
 * Os ids dos centros do recorte, ou `null` quando não há recorte (todos). "Meus centros": os centros
 * de que a pessoa é gestora e os do Master que é dela; num Master com referência organizacional, os
 * de baixo são da referência, não do gestor. Master ou Gerencial sem centro escolhido: nenhum.
 */
export function costCenterScopeIds(
  nodes: CostCenterStructureNode[],
  scope: CostCenterScope | undefined,
  scopeId: string | null | undefined,
  viewer: CostCenterViewer,
): Set<string> | null {
  if (!scope || scope === 'all') return null;
  const placement = costCenterPlacement(nodes);
  const ids = new Set<string>();
  const isMine = (n: CostCenterStructureNode, pl: CostCenterPlacement) => {
    if (viewer.userId && n.managerUserId === viewer.userId) return true;
    if (pl.masterReferencePersonId) return pl.masterReferencePersonId === viewer.personId;
    return !!viewer.userId && pl.masterManagerUserId === viewer.userId;
  };
  for (const n of nodes) {
    const pl = placement.get(n.id)!;
    const hit =
      scope === 'mine'
        ? isMine(n, pl)
        : scope === 'master'
          ? !!scopeId && pl.masterId === scopeId
          : !!scopeId && pl.managerialId === scopeId;
    if (hit) ids.add(n.id);
  }
  return ids;
}
